import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { appConfig } from 'shared';
import { HelpText } from '~/modules/common/help-text';
import { ToolCard } from '~/modules/common/tool-card';
import { notificationPreferencesQueryOptions, useUpdateNotificationPreferences } from '~/modules/notification/query';
import { usePushSubscription } from '~/modules/notification/use-push-subscription';
import { FieldContent, FieldLabel, FieldLayout, FieldTitle } from '~/modules/ui/field';
import { Label } from '~/modules/ui/label';
import { RadioGroup, RadioGroupItem } from '~/modules/ui/radio-group';
import { Switch } from '~/modules/ui/switch';
import { tw } from '~/utils/tw';

type DigestFrequency = 'off' | 'daily' | 'weekly';

/** Cadences are mutually exclusive: one enum column, and each notification is digested once. */
const digestOptions = [
  { value: 'off', label: 'c:notifications.digest_off' },
  { value: 'daily', label: 'c:notifications.digest_daily' },
  { value: 'weekly', label: 'c:notifications.digest_weekly' },
] as const satisfies { value: DigestFrequency; label: string }[];

const cardClass = tw('mx-auto sm:w-full');

/** Only email is opt-out: the inbox always fills, so every switch off still delivers the mention. */
export function AccountNotificationsCard() {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(notificationPreferencesQueryOptions());
  const { mutate } = useUpdateNotificationPreferences();
  const push = usePushSubscription();

  return (
    <ToolCard label="c:notifications" description={t('c:notifications.text')} className={cardClass}>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Switch id="mentionEmail" checked={data.mentionEmail} onCheckedChange={(mentionEmail) => mutate({ mentionEmail })} />
          <Label htmlFor="mentionEmail">{t('c:notifications.mention_email')}</Label>
        </div>

        {appConfig.has.commentEmail && (
          <div className="flex items-center gap-4">
            <Switch id="commentEmail" checked={data.commentEmail} onCheckedChange={(commentEmail) => mutate({ commentEmail })} />
            <Label htmlFor="commentEmail">{t('c:notifications.comment_email')}</Label>
          </div>
        )}

        {push.supported && (
          <div className="flex items-center gap-4">
            <Switch
              id="pushEnabled"
              checked={push.enabled}
              disabled={push.busy}
              onCheckedChange={(checked) => (checked ? push.enable() : push.disable())}
            />
            <Label htmlFor="pushEnabled">{t('c:notifications.push')}</Label>
          </div>
        )}

        <div className="mt-3 flex flex-col gap-4">
          <HelpText content={t('c:notifications.digest.text')} className="mb-0">
            <Label id="digest-label">{t('c:notifications.digest')}</Label>
          </HelpText>
          {/* One tile per cadence: a bare `grid` is already the stacked column, so only the desktop row needs a class */}
          <RadioGroup
            aria-labelledby="digest-label"
            value={data.digest}
            onValueChange={(digest) => mutate({ digest: digest as DigestFrequency })}
            className="lg:grid-cols-3"
          >
            {digestOptions.map(({ value, label }) => (
              <FieldLabel key={value} htmlFor={`digest-${value}`} className="cursor-pointer">
                <FieldLayout orientation="horizontal">
                  <RadioGroupItem id={`digest-${value}`} value={value} />
                  <FieldContent>
                    <FieldTitle>{t(label)}</FieldTitle>
                  </FieldContent>
                </FieldLayout>
              </FieldLabel>
            ))}
          </RadioGroup>
        </div>
      </div>
    </ToolCard>
  );
}
